import { FC, HTMLAttributes, Suspense } from 'react';
import styled, { useTheme } from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { CreditsCard } from '../CreditsCard';
import { GameId, GameIdProps } from '../GameId';
import { MoreButton } from '../MoreButton';
import { PrebuyButton } from '../PrebuyButton';
import { TabHeader } from '../TabHeader';
import { FpeTokenButton } from '../FpeTokenButton';
import {
    LazyBingoMarketingModal,
    type BingoMarketingModalProps,
} from './BingoMarketingModal.lazy';

export type HeaderProps = HTMLAttributes<HTMLDivElement> &
    GameIdProps & {
        isOpen: boolean;
        isMax: boolean;
        cash: string;
        bonus: string;
        total: string;
        tokens?: number;
        currency: string;
        currencyUnit: 'cash' | 'points';
        username: string;
        hideSideGames: boolean;
        hideCurrencyToggle: boolean;
        marketingModalConfig: Omit<
            BingoMarketingModalProps,
            'isOpen' | 'onClose'
        > | null;
        onMarketingModalToggle: () => void;
        onSettingsOpen: () => void;
        onToggleCurrency: () => void;
        onToggleScreen: () => void;
    };

const HeaderContainer = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin: 0 1.25rem 0.875rem;
`;

const CreditsCardContainer = styled.div`
    display: flex;
    justify-content: space-around;
    width: 80%;
    gap: 1.7rem;
    padding-left: 1rem;
    padding-right: 1rem;
`;

export const HeaderMobile: FC<HeaderProps> = ({
    currencyUnit,
    isOpen,
    gameId,
    cash,
    bonus,
    tokens = 0,
    hideSideGames,
    marketingModalConfig,
    onMarketingModalToggle,
    onSettingsOpen,
}) => {
    const { t } = useTranslation();
    const location = useLocation();
    const theme = useTheme();

    return (
        <>
            {location.pathname !== '/side' && (
                <>
                    <GameId gameId={gameId} />
                    <HeaderContainer>
                        <MoreButton handleClick={onSettingsOpen} />
                        <CreditsCardContainer>
                            {!theme.credit.hide?.cash && (
                                <div style={{ flex: 1 }}>
                                    <CreditsCard
                                        type="CASH"
                                        title={t('nav.' + currencyUnit)}
                                        body={cash}
                                    />
                                </div>
                            )}
                            {!theme.credit.hide?.bonus && (
                                <div style={{ flex: 1 }}>
                                    <CreditsCard
                                        type="BONUS"
                                        title={t('nav.bonus')}
                                        body={bonus}
                                    />
                                </div>
                            )}
                            <FpeTokenButton token={tokens || 0} />
                        </CreditsCardContainer>
                        <PrebuyButton handleClick={onMarketingModalToggle} />
                    </HeaderContainer>
                    {marketingModalConfig && (
                        <Suspense fallback={null}>
                            <LazyBingoMarketingModal
                                {...marketingModalConfig}
                                isOpen={isOpen}
                                onClose={onMarketingModalToggle}
                            />
                        </Suspense>
                    )}
                </>
            )}
            <TabHeader hideSideGames={hideSideGames} />
        </>
    );
};
